import { FEATURE_AVAILABILITY_TYPE_CODE } from "@/v2-common/featureSurface/featureSurface.constants"
import type { CheckedBy } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.checkedBy.utils"
import type { FeatureGroupPickedLike } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import type { FeatureBrowseQuery } from "@/v2-backoffice-client/license/featureGroup/compose/__types__/license.featureGroup.browse.query"
import type { FeatureBrowseGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/compose/__types__/license.featureGroup.browseGroups.query"

export type RegistryRow = FeatureBrowseQuery["browseFeatures"][number]
export type RegistryGroup =
  FeatureBrowseGroupsQuery["getFeatureBrowseGroups"][number]

export type RegistryType = { code: number; name: string; count: number }

/** The feature types the groups fold into, with the features each holds. */
export function typesOf(groups: readonly RegistryGroup[]): RegistryType[] {
  const byCode = new Map<number, RegistryType>()
  for (const group of groups) {
    const entry = byCode.get(group.FeatureTypeCode) ?? {
      code: group.FeatureTypeCode,
      name: group.FeatureTypeName,
      count: 0,
    }
    entry.count += group.FeatureCount
    byCode.set(group.FeatureTypeCode, entry)
  }
  return [...byCode.values()]
}

/** Where the pick lands and what it does for a user. */
export function effectOf(row: RegistryRow, checkedBy: CheckedBy) {
  if (checkedBy.kind === "console") {
    return "a capability — it lands in the Capabilities view"
  }
  if (checkedBy.kind === "nobody") {
    return "nothing checks this code yet: granting it changes nothing for a user today"
  }
  if (
    row.FeatureAvailabilityTypeCode ===
    FEATURE_AVAILABILITY_TYPE_CODE.lockedWithinPackage
  ) {
    return "package-only — only a package carrying it unlocks it"
  }
  return "available to everyone already; the tick is a placement for the day it is made package-only"
}

export function toPicked(row: RegistryRow): FeatureGroupPickedLike {
  return {
    Code: row.Code,
    Name: row.Name,
    FeatureTypeCode: row.FeatureTypeCode,
    FeatureTypeName: row.FeatureTypeName,
    IsSurface: row.IsSurface,
    CheckedBy: row.CheckedBy,
    CheckedHow: row.CheckedHow,
  }
}

/** Every pane's row: a full-width button with a name and a count. */
export const REGISTRY_ITEM_CLASS =
  "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-muted/60"

/** Every pane: fills the browser's height and scrolls on its own. */
export const REGISTRY_PANE_CLASS = "min-h-0 overflow-y-auto"
